import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { decodeDeep } from './lib/text';
import '@/App.css';
import { toast, Toaster } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import { initVersionCheck, stopVersionCheck, reloadApp } from './utils/versionCheck';
import { isAdmin, canSwitchOrg, canSeeOrganisations } from './utils/roles';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TicketDetailsPage = lazy(() => import('./pages/TicketDetailsPage'));
const ActivityDashboard = lazy(() => import('./pages/ActivityDashboard'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ClientAdminDashboard = lazy(() => import('./pages/ClientAdminDashboard'));
const OrganizationsPage = lazy(() => import('./pages/OrganizationsPage'));
const YouPage = lazy(() => import('./pages/YouPage'));

// Loading spinner component
const PageLoader = () => (
  <div
    className="flex items-center justify-center min-h-screen"
    style={{ background: 'var(--bg)' }}
  >
    <div className="flex flex-col items-center gap-3">
      <div
        className="w-8 h-8 rounded-full animate-spin"
        style={{
          border: '3px solid var(--rail-border)',
          borderTopColor: 'var(--navy-mid)',
        }}
      />
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted-fg)' }}>Loading…</div>
    </div>
  </div>
);

// Protected Route Component for Admin Areas - uses JWT role check
const ProtectedRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

const AdminProtectedRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  const location = useLocation();
  if (!user || !isAdmin(user.role)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.timeout = 30000; // 30 second timeout

// Add axios request interceptor to attach X-Org-Id header
axios.interceptors.request.use(config => {
  const orgId = localStorage.getItem('currentOrgId');
  if (orgId) {
    config.headers['X-Org-Id'] = orgId;
  }
  return config;
});

// Add axios interceptor for global error handling
axios.interceptors.response.use(
  response => {
    if (response.data && typeof response.data === 'object') {
      response.data = decodeDeep(response.data);
    }
    return response;
  },
  error => {
    // Handle timeout
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
    }
    // Handle unauthorized (token expired)
    else if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      console.warn('Token expired, redirecting to login');
      sessionStorage.clear();
      window.location.href = '/login';
    }
    // Handle server errors
    else if (error.response?.status >= 500) {
      console.error('Server error:', error.response?.status);
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const switchOrg = (orgId) => {
    const org = availableOrgs.find(o => o.id === orgId);
    setCurrentOrg(org || null);
    if (orgId) {
      localStorage.setItem('currentOrgId', orgId);
    } else {
      localStorage.removeItem('currentOrgId');
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      // Verify token
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        const userData = response.data;
        setUser(userData);
        // Set org from user data
        if (userData.organization) {
          setCurrentOrg(userData.organization);
          if (!localStorage.getItem('currentOrgId')) {
            localStorage.setItem('currentOrgId', userData.organization.id);
          }
        }
        // Fetch available orgs for super/global admin
        if (canSwitchOrg(userData.role)) {
          axios.get(`${API}/organizations`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            const orgs = res.data.organizations || [];
            setAvailableOrgs(orgs);
            // Reconcile currentOrg with sessionStorage (e.g. after org switch + reload)
            const storedOrgId = localStorage.getItem('currentOrgId');
            if (storedOrgId) {
              const matchedOrg = orgs.find(o => o.id === storedOrgId);
              if (matchedOrg) {
                setCurrentOrg(matchedOrg);
              }
            }
          }).catch(() => {});
        }
        setLoading(false);
      })
      .catch(() => {
        sessionStorage.removeItem('token');
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  // Initialize version checking when app loads
  useEffect(() => {
    const handleNewVersion = (newVersion) => {
      toast.info(
        'A new version is available! Click to refresh.',
        {
          duration: Infinity, // Don't auto-dismiss
          action: {
            label: 'Refresh Now',
            onClick: () => reloadApp()
          },
          onDismiss: () => {
            // Show a less intrusive reminder after 30 seconds
            setTimeout(() => {
              toast('Please refresh to get the latest version', {
                duration: 10000,
                action: {
                  label: 'Refresh',
                  onClick: () => reloadApp()
                }
              });
            }, 30000);
          }
        }
      );
    };

    initVersionCheck(handleNewVersion);

    // Cleanup on unmount
    return () => {
      stopVersionCheck();
    };
  }, []);

  const login = async (token, userData) => {
    sessionStorage.setItem('token', token);
    setUser(userData);

    // For org-switching roles, restore last selected org from localStorage
    const savedOrgId = localStorage.getItem('currentOrgId');
    if (savedOrgId && canSwitchOrg(userData.role)) {
      try {
        const res = await axios.get(`${API}/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const orgs = res.data.organizations || [];
        setAvailableOrgs(orgs);
        const matchedOrg = orgs.find(o => o.id === savedOrgId);
        if (matchedOrg) {
          setCurrentOrg(matchedOrg);
          return;
        }
      } catch (err) {
        console.warn('Failed to fetch orgs on login:', err);
      }
    }

    // Fallback: use the org from the login response
    if (userData.organization) {
      setCurrentOrg(userData.organization);
      localStorage.setItem('currentOrgId', userData.organization.id);
    }
  };

  const logout = async () => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, { token }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.warn('Logout API call failed:', error);
      }
    }
    sessionStorage.removeItem('token');
    // Keep currentOrgId in localStorage so it persists across login sessions
    setUser(null);
    setCurrentOrg(null);
    setAvailableOrgs([]);
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: 'var(--bg)' }}
      >
        <div className="font-display" style={{ fontSize: 17, color: 'var(--muted-fg)' }}>Loading…</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, login, logout, currentOrg, setCurrentOrg, availableOrgs, setAvailableOrgs, switchOrg }}>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
            <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
            <Route path="/signup" element={user ? <Navigate to="/" /> : <SignupPage />} />
            <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPassword />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            {/* The mobile tab bar's fourth destination. */}
            <Route path="/you" element={<ProtectedRoute><YouPage /></ProtectedRoute>} />
            <Route
              path="/"
              element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
            />
            <Route
              path="/tickets/:id"
              element={<ProtectedRoute><TicketDetailsPage /></ProtectedRoute>}
            />
            {/* The old admin-login page compared a password compiled into the
                public JS bundle and set a sessionStorage flag nothing ever read.
                /activity is protected by real authentication, so this now just
                forwards there. */}
            <Route
              path="/admin-login"
              element={<Navigate to="/activity" replace />}
            />
            <Route
              path="/client-dashboard"
              element={user ? <ClientAdminDashboard /> : <Navigate to="/login" />}
            />
            <Route
              path="/organizations"
              element={
                canSeeOrganisations(user?.role) ? (
                  <OrganizationsPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/activity"
              element={
                isAdmin(user?.role) ? (
                  <AdminProtectedRoute>
                    <ActivityDashboard />
                  </AdminProtectedRoute>
                ) : (
                  <Navigate to="/" />
                )
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
